import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, LogOut } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import DateInput from '@/components/DateInput';
import { handleApiError } from '@/utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const DEPARTAMENTOS_MAP = {
  'Peón agrícola labores livianas': ['Producción', 'Mantenimiento'],
  'Chofer': ['Conductor de Vehículo Pesado', 'Conductor de Vehículo Liviano', 'Conductor de Maquinaria Especial o Agrícola'],
  'Mecánico': ['Taller Mecánico', 'Mantenimiento'],
  'Administrativo': ['Administrador'],
  'Otro': ['Otro']
};

const LABOR_ESPECIFICA_MAP = {
  'Producción': ['Cortero', 'Ajilador', 'Boyero', 'Coyolero'],
  'Mantenimiento': ['Podador', 'Guadañero', 'Mantenimiento General']
};

export default function EditarTrabajador({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    loadTrabajador();
    loadEmpresas();
  }, [id]);

  const loadEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas`);
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };

  const loadTrabajador = async () => {
    try {
      const response = await axios.get(`${API}/trabajadores/${id}`);
      setFormData(response.data);
      if (response.data.profile_picture) {
        setProfilePicturePreview(`${BACKEND_URL}${response.data.profile_picture}`);
      }
    } catch (error) {
      toast.error('Error al cargar trabajador');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = async () => {
    if (formData.profile_picture) {
      try {
        await axios.delete(`${API}/trabajadores/${id}/profile-picture`);
        setFormData({ ...formData, profile_picture: null });
        setProfilePicturePreview(null);
        setProfilePicture(null);
        toast.success('Foto de perfil eliminada');
      } catch (error) {
        toast.error('Error al eliminar foto de perfil');
      }
    } else {
      setProfilePicture(null);
      setProfilePicturePreview(null);
    }
  };

  const handleDiasLaboralesChange = (dia, checked) => {
    let newDias = [...formData.dias_laborales];
    if (checked) {
      newDias.push(dia);
    } else {
      newDias = newDias.filter(d => d !== dia);
    }
    updateFormData('dias_laborales', newDias);
    
    const diaNoSeleccionado = DIAS_SEMANA.find(d => !newDias.includes(d));
    if (diaNoSeleccionado) {
      updateFormData('dia_libre', diaNoSeleccionado);
    }
  };

  const handleGarantiasSocialesChange = (garantia, checked) => {
    let newGarantias = [...formData.garantias_sociales];
    if (checked) {
      newGarantias.push(garantia);
    } else {
      newGarantias = newGarantias.filter(g => g !== garantia);
    }
    updateFormData('garantias_sociales', newGarantias);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/trabajadores/${id}?updated_by=${user.username}`, formData);
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formDataPic = new FormData();
        formDataPic.append('file', profilePicture);
        await axios.post(`${API}/trabajadores/${id}/profile-picture?uploaded_by=${user.username}`, formDataPic, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Trabajador actualizado exitosamente');
      navigate('/');
    } catch (error) {
      handleApiError(error, toast.error, 'Error al actualizar trabajador');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="text-emerald-800 text-xl">Cargando datos del trabajador...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-emerald-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-xl font-bold text-emerald-900">Editar Trabajador</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0 }}
          >
          <Card className="border-0 shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="text-2xl text-emerald-900">Datos Personales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Profile Picture Upload */}
              <div className="space-y-2">
                <Label>Foto de Perfil (Opcional)</Label>
                <div className="flex items-center gap-4">
                  {profilePicturePreview ? (
                    <div className="relative">
                      <img 
                        src={profilePicturePreview} 
                        alt="Preview" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-emerald-200"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2 rounded-full w-6 h-6 p-0"
                        onClick={removeProfilePicture}
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">
                        {formData.nombre && formData.primer_apellido 
                          ? `${formData.nombre[0]}${formData.primer_apellido[0]}` 
                          : '?'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      data-testid="edit-trabajador-profile-picture-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG o GIF (Máx. 5MB)</p>
                  </div>
                </div>
              </div>
              
              {/* Código de Sistema y Fecha de Registro */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.codigo_sistema && (
                  <div className="space-y-2 bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <Label className="text-purple-700 font-semibold">Código de Sistema</Label>
                    <Input
                      value={formData.codigo_sistema}
                      disabled
                      className="bg-white font-bold text-purple-700"
                      data-testid="codigo-sistema-display"
                    />
                  </div>
                )}
                {formData.fecha_registro && (
                  <div className="space-y-2 bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <Label className="text-emerald-700">Fecha de Registro</Label>
                    <Input
                      value={formData.fecha_registro}
                      disabled
                      className="bg-white font-semibold text-emerald-700"
                      data-testid="fecha-registro-display"
                    />
                  </div>
                )}
              </div>
              
              {/* Tipo de Trabajador */}
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                <Label className="text-blue-800 font-semibold text-base">Tipo de Trabajador *</Label>
                <Select
                  value={formData.tipo_trabajador || 'permanente'}
                  onValueChange={(value) => updateFormData('tipo_trabajador', value)}
                >
                  <SelectTrigger className="bg-white border-blue-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permanente">🏢 Trabajador Permanente (Todos los datos requeridos)</SelectItem>
                    <SelectItem value="temporal">⏱️ Trabajador Temporal/Ocasional (Solo nombre y ocupación obligatorios)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tipo_trabajador === 'temporal' && (
                  <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                    <span className="inline-block bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded">
                      TEMPORAL/OCASIONAL
                    </span>
                    Solo nombre y ocupación son obligatorios. Los demás campos son opcionales.
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => updateFormData('nombre', e.target.value)}
                    data-testid="nombre-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primer Apellido {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    value={formData.primer_apellido}
                    onChange={(e) => updateFormData('primer_apellido', e.target.value)}
                    data-testid="primer-apellido-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Segundo Apellido {formData.tipo_trabajador === 'permanente' ? '' : '(Opcional)'}</Label>
                  <Input
                    value={formData.segundo_apellido}
                    onChange={(e) => updateFormData('segundo_apellido', e.target.value)}
                    data-testid="segundo-apellido-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Identificación {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Select value={formData.tipo_identificacion} onValueChange={(v) => updateFormData('tipo_identificacion', v)}>
                    <SelectTrigger data-testid="tipo-identificacion-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CN">CN - Cédula Nacional</SelectItem>
                      <SelectItem value="CR">CR - Cédula de Residencia</SelectItem>
                      <SelectItem value="P">P - Pasaporte</SelectItem>
                      <SelectItem value="PL">PL - Permiso Laboral</SelectItem>
                      <SelectItem value="SD">SD - Sin Documentos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Número de Identificación {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    value={formData.numero_identificacion}
                    onChange={(e) => updateFormData('numero_identificacion', e.target.value)}
                    data-testid="numero-identificacion-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de Nacimiento {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <DateInput
                    value={formData.fecha_nacimiento}
                    onChange={(value) => updateFormData('fecha_nacimiento', value)}
                    data-testid="fecha-nacimiento-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nacionalidad {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Select value={formData.nacionalidad} onValueChange={(v) => updateFormData('nacionalidad', v)}>
                    <SelectTrigger data-testid="nacionalidad-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                      <SelectItem value="Nicaragua">Nicaragua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Sexo *</Label>
                  <Select value={formData.sexo} onValueChange={(v) => updateFormData('sexo', v)}>
                    <SelectTrigger data-testid="sexo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Femenino">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado Civil *</Label>
                  <Select value={formData.estado_civil} onValueChange={(v) => updateFormData('estado_civil', v)}>
                    <SelectTrigger data-testid="estado-civil-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Soltero">Soltero(a)</SelectItem>
                      <SelectItem value="Casado">Casado(a)</SelectItem>
                      <SelectItem value="Unión Libre">Unión Libre</SelectItem>
                      <SelectItem value="Divorciado">Divorciado(a)</SelectItem>
                      <SelectItem value="Viudo">Viudo(a)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Escolaridad *</Label>
                  <Select value={formData.escolaridad} onValueChange={(v) => updateFormData('escolaridad', v)}>
                    <SelectTrigger data-testid="escolaridad-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Primaria Incompleta">Primaria Incompleta</SelectItem>
                      <SelectItem value="Primaria Completa">Primaria Completa</SelectItem>
                      <SelectItem value="Secundaria Incompleta">Secundaria Incompleta</SelectItem>
                      <SelectItem value="Secundaria Completa">Secundaria Completa</SelectItem>
                      <SelectItem value="Universidad Incompleta">Universidad Incompleta</SelectItem>
                      <SelectItem value="Universidad Completa">Universidad Completa</SelectItem>
                      <SelectItem value="Ninguna">Ninguna</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lugar de Residencia {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                <Input
                  value={formData.lugar_residencia}
                  onChange={(e) => updateFormData('lugar_residencia', e.target.value)}
                  data-testid="lugar-residencia-input"
                  required={formData.tipo_trabajador === 'permanente'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => updateFormData('telefono', e.target.value)}
                    data-testid="telefono-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Correo Electrónico {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    type="email"
                    value={formData.correo_electronico}
                    onChange={(e) => updateFormData('correo_electronico', e.target.value)}
                    data-testid="correo-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre Contacto Emergencia {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    value={formData.nombre_contacto_emergencia}
                    onChange={(e) => updateFormData('nombre_contacto_emergencia', e.target.value)}
                    data-testid="nombre-contacto-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Parentesco *</Label>
                  <Select value={formData.parentesco_contacto_emergencia} onValueChange={(v) => updateFormData('parentesco_contacto_emergencia', v)}>
                    <SelectTrigger data-testid="parentesco-contacto-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Conyugue">Conyugue</SelectItem>
                      <SelectItem value="Madre/Padre">Madre/Padre</SelectItem>
                      <SelectItem value="Hermana/Hermano">Hermana/Hermano</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Teléfono Contacto Emergencia {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                <Input
                  value={formData.telefono_contacto_emergencia}
                  onChange={(e) => updateFormData('telefono_contacto_emergencia', e.target.value)}
                  data-testid="telefono-contacto-input"
                  required={formData.tipo_trabajador === 'permanente'}
                />
              </div>
            </CardContent>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Card className="border-0 shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="text-2xl text-emerald-900">Información Laboral</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de Ingreso {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <DateInput
                    value={formData.fecha_ingreso}
                    onChange={(value) => updateFormData('fecha_ingreso', value)}
                    data-testid="fecha-ingreso-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lugar de Trabajo {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Select value={formData.lugar_trabajo} onValueChange={(v) => updateFormData('lugar_trabajo', v)}>
                    <SelectTrigger data-testid="lugar-trabajo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Finca Savegre">Finca Savegre</SelectItem>
                      <SelectItem value="Finca Quepos">Finca Quepos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Contrato *</Label>
                  <Select value={formData.tipo_contrato || ''} onValueChange={(v) => updateFormData('tipo_contrato', v)}>
                    <SelectTrigger data-testid="tipo-contrato-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CTI">Contrato a Tiempo Indefinido (CTI)</SelectItem>
                      <SelectItem value="CTD">Contrato a Tiempo Determinado (CTD)</SelectItem>
                      <SelectItem value="OD">Obra Determinada (OD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Empresa *</Label>
                  {empresas.length === 0 ? (
                    <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                      No hay empresas registradas
                    </div>
                  ) : (
                    <Select value={formData.empresa_id || ''} onValueChange={(v) => updateFormData('empresa_id', v)}>
                      <SelectTrigger data-testid="empresa-select">
                        <SelectValue placeholder="Seleccione empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {empresas.map((empresa) => (
                          <SelectItem key={empresa.id} value={empresa.id}>
                            {empresa.nombre_comercial} {empresa.es_principal && '★'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Ocupación *</Label>
                  <Select 
                    value={formData.ocupacion} 
                    onValueChange={(v) => {
                      updateFormData('ocupacion', v);
                      // Reset departamento y labor_especifica cuando cambia ocupación
                      const departamentos = DEPARTAMENTOS_MAP[v] || [];
                      if (departamentos.length > 0) {
                        updateFormData('departamento', departamentos[0]);
                      }
                      updateFormData('labor_especifica', '');
                    }}
                  >
                    <SelectTrigger data-testid="ocupacion-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Peón agrícola labores livianas">Peón agrícola labores livianas</SelectItem>
                      <SelectItem value="Chofer">Chofer</SelectItem>
                      <SelectItem value="Mecánico">Mecánico</SelectItem>
                      <SelectItem value="Administrativo">Administrativo</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Departamento *</Label>
                  <Select 
                    value={formData.departamento} 
                    onValueChange={(v) => {
                      updateFormData('departamento', v);
                      // Reset labor_especifica cuando cambia departamento
                      const labores = LABOR_ESPECIFICA_MAP[v] || [];
                      if (labores.length > 0) {
                        updateFormData('labor_especifica', labores[0]);
                      } else {
                        updateFormData('labor_especifica', '');
                      }
                    }}
                  >
                    <SelectTrigger data-testid="departamento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(DEPARTAMENTOS_MAP[formData.ocupacion] || []).map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Labor Específica *</Label>
                  <Select value={formData.labor_especifica} onValueChange={(v) => updateFormData('labor_especifica', v)}>
                    <SelectTrigger data-testid="labor-especifica-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.departamento && LABOR_ESPECIFICA_MAP[formData.departamento] ? (
                        LABOR_ESPECIFICA_MAP[formData.departamento].map((labor) => (
                          <SelectItem key={labor} value={labor}>{labor}</SelectItem>
                        ))
                      ) : (
                        <SelectItem value={formData.labor_especifica || 'Otro'}>{formData.labor_especifica || 'Otro'}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={formData.status} onValueChange={(v) => updateFormData('status', v)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activo">Activo</SelectItem>
                    <SelectItem value="Despedido">Despedido</SelectItem>
                    <SelectItem value="Liquidado">Liquidado</SelectItem>
                    <SelectItem value="Renunció">Renunció</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Jornada Laboral *</Label>
                  <Select value={formData.jornada_laboral} onValueChange={(v) => updateFormData('jornada_laboral', v)}>
                    <SelectTrigger data-testid="jornada-laboral-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="JOD">Jornada Ordinaria Diurna (JOD)</SelectItem>
                      <SelectItem value="JON">Jornada Ordinaria Nocturna (JON)</SelectItem>
                      <SelectItem value="JOM">Jornada Ordinaria Mixta (JOM)</SelectItem>
                      <SelectItem value="JOA">Jornada Acumulativa (JOA)</SelectItem>
                      <SelectItem value="JOE">Jornada Extraordinaria (JOE)</SelectItem>
                      <SelectItem value="DESTAJO">A Destajo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Manera de Jornada *</Label>
                  <Select value={formData.manera_jornada || ''} onValueChange={(v) => updateFormData('manera_jornada', v)}>
                    <SelectTrigger data-testid="manera-jornada-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Continua">Continua</SelectItem>
                      <SelectItem value="Discontinua">Discontinua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Horario de Trabajo *</Label>
                <Select value={formData.horario_trabajo} onValueChange={(v) => updateFormData('horario_trabajo', v)}>
                  <SelectTrigger data-testid="horario-trabajo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Medio Tiempo">Medio Tiempo</SelectItem>
                    <SelectItem value="Tiempo Completo">Tiempo Completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hora de Entrada</Label>
                  <Input
                    type="time"
                    value={formData.hora_entrada || ''}
                    onChange={(e) => updateFormData('hora_entrada', e.target.value)}
                    data-testid="hora-entrada-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora de Salida</Label>
                  <Input
                    type="time"
                    value={formData.hora_salida || ''}
                    onChange={(e) => updateFormData('hora_salida', e.target.value)}
                    data-testid="hora-salida-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Días Laborales *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {DIAS_SEMANA.map(dia => (
                    <div key={dia} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`edit-${dia}`}
                        checked={formData.dias_laborales.includes(dia)}
                        onCheckedChange={(checked) => handleDiasLaboralesChange(dia, checked)}
                        data-testid={`dia-${dia.toLowerCase()}`}
                      />
                      <Label htmlFor={`edit-${dia}`} className="cursor-pointer">{dia}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Día Libre</Label>
                <Input
                  value={formData.dia_libre}
                  disabled
                  className="bg-gray-100"
                  data-testid="dia-libre-display"
                />
              </div>

              <div className="space-y-2">
                <Label>Garantías Sociales * (Obligatorias por Ley)</Label>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="edit-ccss"
                        checked={formData.garantias_sociales.includes('CCSS')}
                        onCheckedChange={(checked) => handleGarantiasSocialesChange('CCSS', checked)}
                        data-testid="garantia-ccss"
                      />
                      <Label htmlFor="edit-ccss" className="cursor-pointer">CCSS</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="edit-ins"
                        checked={formData.garantias_sociales.includes('INS')}
                        onCheckedChange={(checked) => handleGarantiasSocialesChange('INS', checked)}
                        data-testid="garantia-ins"
                      />
                      <Label htmlFor="edit-ins" className="cursor-pointer">INS</Label>
                    </div>
                  </div>
                  
                  {!formData.garantias_sociales.includes('CCSS') && (
                    <div className="mt-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <Label className="text-yellow-900">¿Por qué NO tiene CCSS? (Obligatorio por Ley)</Label>
                      <Input
                        className="mt-2"
                        value={formData.razon_sin_ccss || ''}
                        onChange={(e) => updateFormData('razon_sin_ccss', e.target.value)}
                        placeholder="Explica la razón..."
                        data-testid="razon-sin-ccss"
                      />
                    </div>
                  )}
                  
                  {!formData.garantias_sociales.includes('INS') && (
                    <div className="mt-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <Label className="text-orange-900">¿Por qué NO tiene INS? (Obligatorio por Ley)</Label>
                      <Input
                        className="mt-2"
                        value={formData.razon_sin_ins || ''}
                        onChange={(e) => updateFormData('razon_sin_ins', e.target.value)}
                        placeholder="Explica la razón..."
                        data-testid="razon-sin-ins"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Talla de Botas {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                <Input
                  value={formData.talla_botas}
                  onChange={(e) => updateFormData('talla_botas', e.target.value)}
                  data-testid="talla-botas-input"
                  required={formData.tipo_trabajador === 'permanente'}
                />
              </div>
            </CardContent>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-emerald-900">Salario y Forma de Pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Salario Mínimo {formData.tipo_trabajador === 'permanente' ? '*' : '(Opcional)'}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.salario_minimo}
                    onChange={(e) => updateFormData('salario_minimo', parseFloat(e.target.value) || 0)}
                    data-testid="salario-minimo-input"
                    required={formData.tipo_trabajador === 'permanente'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fijación Salarial *</Label>
                  <Select value={formData.fijacion_salarial} onValueChange={(v) => updateFormData('fijacion_salarial', v)}>
                    <SelectTrigger data-testid="fijacion-salarial-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ordinaria">Ordinaria</SelectItem>
                      <SelectItem value="Mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Forma de Pago *</Label>
                  <Select value={formData.forma_pago} onValueChange={(v) => updateFormData('forma_pago', v)}>
                    <SelectTrigger data-testid="forma-pago-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EFE">EFE - Efectivo</SelectItem>
                      <SelectItem value="TRA">TRA - Transferencia</SelectItem>
                      <SelectItem value="SM">SM - SINPE Móvil</SelectItem>
                      <SelectItem value="CHE">CHE - Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Periodicidad de Pago *</Label>
                  <Select value={formData.periodicidad_pago} onValueChange={(v) => updateFormData('periodicidad_pago', v)}>
                    <SelectTrigger data-testid="periodicidad-pago-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MES">MES - Mensual</SelectItem>
                      <SelectItem value="QUINCENA">QUINCENA - Quincenal</SelectItem>
                      <SelectItem value="BISEMANA">BISEMANA - Bisemanal</SelectItem>
                      <SelectItem value="SEMANA">SEMANA - Semanal</SelectItem>
                      <SelectItem value="DÍA">DÍA - Diaria</SelectItem>
                      <SelectItem value="HORA">HORA - Por Hora</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
          <div className="flex justify-end mt-6">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-button"
            >
              {saving ? 'Guardando...' : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
            </motion.div>
          </div>
          </motion.div>
        </form>
      </main>
    </div>
  );
}
